/**
 * What each row of the skill editor's file tree stands for.
 *
 * The tree view only takes string ids, so an item is encoded to a string at that boundary
 * and decoded back when the tree reports a selection or a drop. Everything else in the editor
 * works with the union below instead of string prefixes.
 */
export type SkillTreeItem =
  | { kind: 'instructions' }
  /** The inline input for a folder being created. */
  | { kind: 'folderDraft' }
  /** The inline input for a text file being created. */
  | { kind: 'fileDraft' }
  | { kind: 'folder'; path: string }
  /** A file the current version already stores, by the path it was saved under. */
  | { kind: 'saved'; path: string }
  /** A text file written in the editor and not saved yet. */
  | { kind: 'text'; path: string }
  /** A PDF uploaded in the editor and not saved yet. */
  | { id: string; kind: 'pdf' }
  /** A copy of one of the user's documents, not saved yet. */
  | { documentId: string; kind: 'snapshot' };

const INSTRUCTIONS_ID = 'instructions';
/**
 * Drafts use their own prefix: `folder:__draft` would be the id of a real folder named
 * `__draft`, and the draft input would replace it.
 */
const FOLDER_DRAFT_ID = 'draft:folder';
const FILE_DRAFT_ID = 'draft:file';
const PDF_PREFIX = 'pdf:';
const SNAPSHOT_PREFIX = 'snapshot:';
const PATH_KINDS = ['folder', 'saved', 'text'] as const;
const PATH_PREFIXES = {
  folder: 'folder:',
  saved: 'saved:',
  text: 'text:',
} as const satisfies Record<(typeof PATH_KINDS)[number], string>;

export const INSTRUCTIONS_TREE_ITEM = { kind: 'instructions' } as const satisfies SkillTreeItem;

/**
 * The string the tree view uses for an item. Saved, text, PDF and snapshot ids keep the
 * `saved:`, `text:`, `pdf:` and `snapshot:` shape the attachment editor matches against.
 */
export function formatSkillTreeItemId(item: SkillTreeItem): string {
  switch (item.kind) {
    case 'instructions':
      return INSTRUCTIONS_ID;
    case 'folderDraft':
      return FOLDER_DRAFT_ID;
    case 'fileDraft':
      return FILE_DRAFT_ID;
    case 'folder':
    case 'saved':
    case 'text':
      return `${PATH_PREFIXES[item.kind]}${item.path}`;
    case 'pdf':
      return `${PDF_PREFIX}${item.id}`;
    case 'snapshot':
      return `${SNAPSHOT_PREFIX}${item.documentId}`;
  }
}

/** The item behind a tree view id, or null for an id the editor did not create. */
export function parseSkillTreeItemId(id: string): SkillTreeItem | null {
  if (id === INSTRUCTIONS_ID) return INSTRUCTIONS_TREE_ITEM;
  if (id === FOLDER_DRAFT_ID) return { kind: 'folderDraft' };
  if (id === FILE_DRAFT_ID) return { kind: 'fileDraft' };
  for (const kind of PATH_KINDS) {
    const prefix = PATH_PREFIXES[kind];
    if (id.startsWith(prefix) && id.length > prefix.length)
      return { kind, path: id.slice(prefix.length) };
  }
  if (id.startsWith(PDF_PREFIX) && id.length > PDF_PREFIX.length)
    return { id: id.slice(PDF_PREFIX.length), kind: 'pdf' };
  if (id.startsWith(SNAPSHOT_PREFIX) && id.length > SNAPSHOT_PREFIX.length)
    return { documentId: id.slice(SNAPSHOT_PREFIX.length), kind: 'snapshot' };
  return null;
}

/** Tree ids of every folder `path` sits in, outermost first. */
export function getSkillAncestorFolderTreeIds(path: string): string[] {
  const parts = path.split('/').slice(0, -1);
  return parts.map((_, index) =>
    formatSkillTreeItemId({ kind: 'folder', path: parts.slice(0, index + 1).join('/') })
  );
}
